/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SortDirection } from "ag-grid-community";
import { catchError, map, Observable, of } from "rxjs";
import { AnomalyDetails } from "../../models/http/anomaly/anomaly-details.model";
import { SingleRowAnomalousApplication } from "../../models/http/anomaly/single-row-anomalous-application.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { PagedResponse } from "../../models/ui/paged-response.model";
import { TableResponse } from "../../models/ui/table-response.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class AnomalyDetectionHttpService {
	private _anomalyDetectionOperations = ENDPOINT_GROUPS.anomalyDetection.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves a paginated list of all anomalous applications for a given tenant.
	 *
	 * @param tenantId - The ID of the tenant.
	 * @param page - The page number to retrieve.
	 * @param size - The number of items per page.
	 * @param sort - (Optional) The field to sort by.
	 * @param applications - (Optional) A list of application names to filter by.
	 * @param direction - (Optional) The direction of sorting, either "asc" or "desc".
	 * @returns An Observable that emits a PagedResponse containing TableAnomalyDetection items.
	 */
	public getAllAnomalousApplication(
		tenantId: string,
		page: number,
		size: number,
		startDate: string,
		endDate?: string,
		sort?: string,
		applications?: string[],
		direction?: SortDirection
	): Observable<TableResponse<PagedResponse<SingleRowAnomalousApplication>>> {
		const pathParams = { tenantId };
		const params = new HttpParams({
			fromObject: {
				page,
				size,
				startDate,
				...(endDate && { endDate }),
				...(sort && { sort }),
				...(applications && { applications }),
				...(direction && { direction })
			}
		});

		return this._communicationService
			.callService(
				this._anomalyDetectionOperations.getAllAnomalousApplications.service,
				HttpMethods.GET,
				{ pathParams, params }
			)
			.pipe(
				map(response => ({ rowData: response, hasError: false })),
				catchError(() =>
					of({
						rowData: {} as PagedResponse<SingleRowAnomalousApplication>,
						hasError: true
					})
				)
			);
	}

	/**
	 * Retrieves anomalies for a specific application within a given date range.
	 *
	 * @param applicationId - The ID of the application to retrieve anomalies for.
	 * @param startDate - The start date for the anomaly search (ISO 8601 format).
	 * @param tenantId - The tenant ID associated with the application.
	 * @param page - The page number for pagination.
	 * @param size - The number of items per page for pagination.
	 * @param endDate - (Optional) The end date for the anomaly search (ISO 8601 format).
	 * @param layerId - (Optional) An array of layer IDs to filter the anomalies.
	 * @param anomalyType - (Optional) An array of anomaly types to filter the results.
	 * @param sort - (Optional) The sorting criteria for the results.
	 * @param applications - (Optional) An array of application IDs to filter the anomalies.
	 * @param direction - (Optional) The direction of sorting, either "asc" for ascending or "desc" for descending.
	 * @returns An Observable that emits the details of the anomalies.
	 */
	public getAnomaliesByApplication(
		applicationId: string,
		startDate: string,
		tenantId: string,
		page: number,
		size: number,
		endDate?: string,
		layerId?: string[],
		anomalyType?: string[],
		sort?: string,
		applications?: string[],
		direction?: SortDirection
	): Observable<AnomalyDetails> {
		const pathParams = { applicationId };
		const params = new HttpParams({
			fromObject: {
				startDate,
				tenantId,
				page,
				size,
				...(endDate && { endDate }),
				...(layerId && { layerId }),
				...(anomalyType && { anomalyType }),
				...(sort && { sort }),
				...(applications && { applications }),
				...(direction && { direction })
			}
		});

		return this._communicationService.callService(
			this._anomalyDetectionOperations.getAnomaliesByApplication.service,
			HttpMethods.GET,
			{ pathParams, params }
		);
	}

	public addUserDeviceByTenant(tenantId: string, payload: string): Observable<string> {
		const pathParams = { tenantId };

		return this._communicationService.callService(
			this._anomalyDetectionOperations.addUserDeviceByTenant.service,
			HttpMethods.POST,
			{ pathParams },
			payload
		);
	}

	public updateUserNotificationSettings(
		tenantId: string,
		activeDark: boolean,
		payload: string,
		activeMail?: boolean
	): Observable<string> {
		const pathParams = { tenantId };
		const params = new HttpParams({
			fromObject: {
				activeDark,
				...(activeMail !== undefined && { activeMail })
			}
		});

		return this._communicationService.callService(
			this._anomalyDetectionOperations.updateUserNotificationSettings.service,
			HttpMethods.POST,
			{ pathParams, params },
			payload
		);
	}
}
